// ─── Types ───────────────────────────────────────────────────────────────────

export interface Param {
  name: string;
  required: boolean;
  type: string;
  location: 'path' | 'query';
  description?: string;
  defaultValue?: string;
}

export interface RouteMeta {
  responseKey: string;
  method: 'GET' | 'POST';
  label: string;
  path: string;
  params: Param[];
  category: string;
  description?: string;
}

// ─── Route registry ──────────────────────────────────────────────────────────

export const CLIENT_CODE = {
  // crypto
  'crypto/tickers': {
    http: `GET https://api.axionquant.com/crypto/tickers?type=spot`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

tickers = client.crypto.tickers(type='spot')
print(tickers)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const tickers = await client.crypto.tickers({type: 'spot'});
console.log(tickers);`,
  },
  'crypto/ticker': {
    http: `GET https://api.axionquant.com/crypto/BTC`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

ticker = client.crypto.ticker('BTC')
print(ticker)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const ticker = await client.crypto.ticker('BTC');
console.log(ticker);`,
  },
  'crypto/prices': {
    http: `GET https://api.axionquant.com/crypto/BTC/prices?frame=daily&from=2025-12-01&to=2025-12-19`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

prices = client.crypto.prices('BTC',
    frame='daily',
    from_date='2025-12-01',
    to_date='2025-12-19'
)
print(prices)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const prices = await client.crypto.prices('BTC', {
  frame: 'daily',
  from: '2025-12-01',
  to: '2025-12-19'
});
console.log(prices);`,
  },

  'crypto/ticker-quote': {
    http: `GET https://api.axionquant.com/crypto/BTC/quote`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

quote = client.crypto.quote('BTC')
print(quote)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const quote = await client.crypto.quote('BTC');
console.log(quote);`,
  },
  'crypto/gainers': {
    http: `GET https://api.axionquant.com/crypto/gainers?days=5&limit=10`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

gainers = client.crypto.gainers(days=5, limit=10)
print(gainers)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const gainers = await client.crypto.gainers({days: 5, limit: 10});
console.log(gainers);`,
  },
  'crypto/losers': {
    http: `GET https://api.axionquant.com/crypto/losers?days=5&limit=10`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

losers = client.crypto.losers(days=5, limit=10)
print(losers)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const losers = await client.crypto.losers({days: 5, limit: 10});
console.log(losers);`,
  },

  // forex
  'forex/tickers': {
    http: `GET https://api.axionquant.com/forex/tickers?country=AE&exchange=IDC`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

tickers = client.forex.tickers(
    country='AE',
    exchange='IDC'
)
print(tickers)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const tickers = await client.forex.tickers({
    country: 'AE',
    exchange: 'IDC'
});
console.log(tickers);`,
  },
  'forex/ticker': {
    http: `GET https://api.axionquant.com/forex/AEDAUD`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

ticker = client.forex.ticker('AEDAUD')
print(ticker)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const ticker = await client.forex.ticker('AEDAUD');
console.log(ticker);`,
  },
  'forex/prices': {
    http: `GET https://api.axionquant.com/forex/AEDAUD/prices?frame=daily&from=2025-12-01&to=2025-12-19`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

prices = client.forex.prices('AEDAUD',
    frame='daily',
    from_date='2025-12-01',
    to_date='2025-12-19'
)
print(prices)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const prices = await client.forex.prices('AEDAUD', {
  frame: 'daily',
  from: '2025-12-01',
  to: '2025-12-19'
});
console.log(prices);`,
  },

  'forex/ticker-quote': {
    http: `GET https://api.axionquant.com/forex/EURUSD/quote`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

quote = client.forex.quote('EURUSD')
print(quote)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const quote = await client.forex.quote('EURUSD');
console.log(quote);`,
  },
  'forex/gainers': {
    http: `GET https://api.axionquant.com/forex/gainers?days=5&limit=10`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

gainers = client.forex.gainers(days=5, limit=10)
print(gainers)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const gainers = await client.forex.gainers({days: 5, limit: 10});
console.log(gainers);`,
  },
  'forex/losers': {
    http: `GET https://api.axionquant.com/forex/losers?days=5&limit=10`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

losers = client.forex.losers(days=5, limit=10)
print(losers)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const losers = await client.forex.losers({days: 5, limit: 10});
console.log(losers);`,
  },

  // indices
  'indices/tickers': {
    http: `GET https://api.axionquant.com/indices/tickers?exchange=ASX`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

tickers = client.indices.tickers(exchange='ASX')
print(tickers)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const tickers = await client.indices.tickers({exchange: 'ASX'});
console.log(tickers);`,
  },
  'indices/ticker': {
    http: `GET https://api.axionquant.com/indices/GRNWATERLEURX`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

ticker = client.indices.ticker('GRNWATERLEURX')
print(ticker)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const ticker = await client.indices.ticker('GRNWATERLEURX');
console.log(ticker);`,
  },
  'indices/prices': {
    http: `GET https://api.axionquant.com/indices/GVZ/prices?frame=daily&from=2025-12-01&to=2025-12-19`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

prices = client.indices.prices('GVZ',
    frame='daily',
    from_date='2025-12-01',
    to_date='2025-12-19'
)
print(prices)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const prices = await client.indices.prices('GVZ', {
  frame: 'daily',
  from: '2025-12-01',
  to: '2025-12-19'
});
console.log(prices);`,
  },

  'indices/ticker-quote': {
    http: `GET https://api.axionquant.com/indices/SPX/quote`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

quote = client.indices.quote('SPX')
print(quote)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const quote = await client.indices.quote('SPX');
console.log(quote);`,
  },
  'indices/gainers': {
    http: `GET https://api.axionquant.com/indices/gainers?days=5&limit=10`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

gainers = client.indices.gainers(days=5, limit=10)
print(gainers)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const gainers = await client.indices.gainers({days: 5, limit: 10});
console.log(gainers);`,
  },
  'indices/losers': {
    http: `GET https://api.axionquant.com/indices/losers?days=5&limit=10`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

losers = client.indices.losers(days=5, limit=10)
print(losers)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const losers = await client.indices.losers({days: 5, limit: 10});
console.log(losers);`,
  },
  'indices/components': {
    http: `GET https://api.axionquant.com/indices/SPX/components`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

components = client.indices.components('SPX')
print(components)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const components = await client.indices.components('SPX');
console.log(components);`,
  },
  'indices/exposure': {
    http: `GET https://api.axionquant.com/indices/AAPL/exposure`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

exposure = client.indices.exposure('AAPL')
print(exposure)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const exposure = await client.indices.exposure('AAPL');
console.log(exposure);`,
  },

  // futures
  'futures/tickers': {
    http: `GET https://api.axionquant.com/futures/tickers?exchange=CME`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

tickers = client.futures.tickers(exchange='CME')
print(tickers)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const tickers = await client.futures.tickers({exchange: 'CME'});
console.log(tickers);`,
  },
  'futures/ticker': {
    http: `GET https://api.axionquant.com/futures/ALI`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

ticker = client.futures.ticker('ALI')
print(ticker)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const ticker = await client.futures.ticker('ALI');
console.log(ticker);`,
  },
  'futures/prices': {
    http: `GET https://api.axionquant.com/futures/ALI/prices?frame=daily&from=2025-12-01&to=2025-12-19`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

prices = client.futures.prices('ALI',
    frame='daily',
    from_date='2025-12-01',
    to_date='2025-12-19'
)
print(prices)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const prices = await client.futures.prices('ALI', {
  frame: 'daily',
  from: '2025-12-01',
  to: '2025-12-19'
});
console.log(prices);`,
  },
  'futures/ticker-quote': {
    http: `GET https://api.axionquant.com/futures/ES/quote`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

quote = client.futures.quote('ES')
print(quote)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const quote = await client.futures.quote('ES');
console.log(quote);`,
  },
  'futures/gainers': {
    http: `GET https://api.axionquant.com/futures/gainers?days=5&limit=10`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

gainers = client.futures.gainers(days=5, limit=10)
print(gainers)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const gainers = await client.futures.gainers({days: 5, limit: 10});
console.log(gainers);`,
  },
  'futures/losers': {
    http: `GET https://api.axionquant.com/futures/losers?days=5&limit=10`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

losers = client.futures.losers(days=5, limit=10)
print(losers)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const losers = await client.futures.losers({days: 5, limit: 10});
console.log(losers);`,
  },

  // stocks
  'stocks/ticker-quote': {
    http: `GET https://api.axionquant.com/stocks/AAPL/quote`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

quote = client.stocks.quote('AAPL')
print(quote)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const quote = await client.stocks.quote('AAPL');
console.log(quote);`,
  },
  'stocks/gainers': {
    http: `GET https://api.axionquant.com/stocks/gainers?days=5&limit=10&market=america`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

gainers = client.stocks.gainers(
    days=5,
    limit=10,
    market='america'
)
print(gainers)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const gainers = await client.stocks.gainers({
    days: 5,
    limit: 10,
    market: 'america'
});
console.log(gainers);`,
  },
  'stocks/losers': {
    http: `GET https://api.axionquant.com/stocks/losers?days=5&limit=10&market=america`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

losers = client.stocks.losers(
    days=5,
    limit=10,
    market='america'
)
print(losers)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const losers = await client.stocks.losers({
    days: 5,
    limit: 10,
    market: 'america'
});
console.log(losers);`,
  },
  'stocks/tickers': {
    http: `GET https://api.axionquant.com/stocks/tickers?country=america&exchange=NASDAQ`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

tickers = client.stocks.tickers(
    country='america',
    exchange='NASDAQ'
)
print(tickers)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const tickers = await client.stocks.tickers({
    country: 'america',
    exchange: 'NASDAQ'
});
console.log(tickers);`,
  },
  'stocks/ticker': {
    http: `GET https://api.axionquant.com/stocks/AAPL`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

ticker = client.stocks.ticker('AAPL')
print(ticker)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const ticker = await client.stocks.ticker('AAPL');
console.log(ticker);`,
  },
  'stocks/prices': {
    http: `GET https://api.axionquant.com/stocks/AAPL/prices?frame=daily&from=2025-12-01&to=2025-12-19`,
    curl: `curl
  -X GET
  -H "Authorization: Bearer axn_123"
  "https://api.axionquant.com/stocks/AAPL/prices?frame=daily&from=2025-12-01&to=2025-12-19"`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

prices = client.stocks.prices('AAPL',
    frame='daily',
    from_date='2025-12-01',
    to_date='2025-12-19'
)
print(prices)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const prices = await client.stocks.prices('AAPL', {
  frame: 'daily',
  from: '2025-12-01',
  to: '2025-12-19'
});
console.log(prices);`,
  },

  //etfs
  'etfs/fund': {
    http: `GET https://api.axionquant.com/etfs/SPY/fund`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

fund = client.etfs.fund('SPY')
print(fund)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const fund = await client.etfs.fund('SPY');
console.log(fund);`,
  },
  'etfs/weights': {
    http: `GET https://api.axionquant.com/etfs/SPY/weights`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

weights = client.etfs.weights('SPY')
print(weights)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const weights = await client.etfs.weights('SPY');
console.log(weights);`,
  },
  'etfs/holdings': {
    http: `GET https://api.axionquant.com/etfs/SPY/holdings`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

holdings = client.etfs.holdings('SPY')
print(holdings)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const holdings = await client.etfs.holdings('SPY');
console.log(holdings);`,
  },
  'etfs/holdings-all': {
    http: `GET https://api.axionquant.com/etfs/SPY/holdings/all`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

holdings = client.etfs.holdings_all('SPY')
print(holdings)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const holdings = await client.etfs.holdingsAll('SPY');
console.log(holdings);`,
  },
  'etfs/tickers': {
    http: `GET https://api.axionquant.com/etfs/tickers?country=america&exchange=NASDAQ`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

tickers = client.etfs.tickers(
    country='america',
    exchange='NASDAQ'
)
print(tickers)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const tickers = await client.etfs.tickers({
    country: 'america',
    exchange: 'NASDAQ'
});
console.log(tickers);`,
  },
  'etfs/ticker': {
    http: `GET https://api.axionquant.com/etfs/SPY`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

ticker = client.etfs.ticker('SPY')
print(ticker)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const ticker = await client.etfs.ticker('SPY');
console.log(ticker);`,
  },
  'etfs/ticker-quote': {
    http: `GET https://api.axionquant.com/etfs/SPY/quote`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

quote = client.etfs.quote('SPY')
print(quote)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const quote = await client.etfs.quote('SPY');
console.log(quote);`,
  },
  'etfs/gainers': {
    http: `GET https://api.axionquant.com/etfs/gainers?days=5&limit=10`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

gainers = client.etfs.gainers(days=5, limit=10)
print(gainers)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const gainers = await client.etfs.gainers({days: 5, limit: 10});
console.log(gainers);`,
  },
  'etfs/losers': {
    http: `GET https://api.axionquant.com/etfs/losers?days=5&limit=10`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

losers = client.etfs.losers(days=5, limit=10)
print(losers)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const losers = await client.etfs.losers({days: 5, limit: 10});
console.log(losers);`,
  },
  'etfs/prices': {
    http: `GET https://api.axionquant.com/etfs/SPY/prices?frame=daily&from=2025-12-01&to=2025-12-19`,
    curl: `curl
  -X GET
  -H "Authorization: Bearer axn_123"
  "https://api.axionquant.com/etfs/SPY/prices?frame=daily&from=2025-12-01&to=2025-12-19"`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

prices = client.etfs.prices('SPY',
    frame='daily',
    from_date='2025-12-01',
    to_date='2025-12-19'
)
print(prices)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const prices = await client.etfs.prices('SPY', {
  frame: 'daily',
  from: '2025-12-01',
  to: '2025-12-19'
});
console.log(prices);`,
  },
  'etfs/exposure': {
    http: `GET https://api.axionquant.com/etfs/AAPL/exposure`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

exposure = client.etfs.exposure('AAPL')
print(exposure)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const exposure = await client.etfs.exposure('AAPL');
console.log(exposure);`,
  },


  // esg
  'esg/ticker': {
    http: `GET https://api.axionquant.com/esg/AAPL`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

esg_data = client.esg.data('AAPL')
print(esg_data)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const esgData = await client.esg.data('AAPL');
console.log(esgData);`,
  },

  // sentiment
  'sentiment/social': {
    http: `GET https://api.axionquant.com/sentiment/AAPL/social`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

sentiment = client.sentiment.social('AAPL')
print(sentiment)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const sentiment = await client.sentiment.social('AAPL');
console.log(sentiment);`,
  },
  'sentiment/news': {
    http: `GET https://api.axionquant.com/sentiment/AAPL/news`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

sentiment = client.sentiment.news('AAPL')
print(sentiment)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const sentiment = await client.sentiment.news('AAPL');
console.log(sentiment);`,
  },
  'sentiment/analyst': {
    http: `GET https://api.axionquant.com/sentiment/AAPL/analyst`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

sentiment = client.sentiment.analyst('AAPL')
print(sentiment)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const sentiment = await client.sentiment.analyst('AAPL');
console.log(sentiment);`,
  },

  // supply-chain
  'supplychain/customers': {
    http: `GET https://api.axionquant.com/supply-chain/AAPL/customers`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

customers = client.supply_chain.customers('AAPL')
print(customers)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const customers = await client.supplyChain.customers('AAPL');
console.log(customers);`,
  },
  'supplychain/peers': {
    http: `GET https://api.axionquant.com/supply-chain/AAPL/peers`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

peers = client.supply_chain.peers('AAPL')
print(peers)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const peers = await client.supplyChain.peers('AAPL');
console.log(peers);`,
  },
  'supplychain/suppliers': {
    http: `GET https://api.axionquant.com/supply-chain/AAPL/suppliers`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

suppliers = client.supply_chain.suppliers('AAPL')
print(suppliers)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const suppliers = await client.supplyChain.suppliers('AAPL');
console.log(suppliers);`,
  },

  // credit
  'credit/search': {
    http: `GET https://api.axionquant.com/credit/search?query=Apple`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

results = client.credit.search('Apple')
print(results)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const results = await client.credit.search('Apple');
console.log(results);`,
  },
  'credit/ratings': {
    http: `GET https://api.axionquant.com/credit/ratings/112354`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

ratings = client.credit.ratings('112354')
print(ratings)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const ratings = await client.credit.ratings('112354');
console.log(ratings);`,
  },


  // econ
  'econ/find': {
    http: `GET https://api.axionquant.com/econ/find?query=Semiconductor+spending`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

results = client.econ.find('Semiconductor spending')
print(results)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const results = await client.econ.find('Semiconductor spending');
console.log(results);`,
  },
  'econ/search': {
    http: `GET https://api.axionquant.com/econ/search?query=Corn`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

results = client.econ.search('Corn')
print(results)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const results = await client.econ.search('Corn');
console.log(results);`,
  },
  'econ/dataset': {
    http: `GET https://api.axionquant.com/econ/dataset/PMAIZMTUSDM`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

dataset = client.econ.dataset('PMAIZMTUSDM')
print(dataset)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const dataset = await client.econ.dataset('PMAIZMTUSDM');
console.log(dataset);`,
  },
  'econ/calendar': {
    http: `GET https://api.axionquant.com/econ/calendar?from=2025-01-01&to=2025-01-02&country=US,AU&minImportance=0&currency=USD&category=employment&limit=10`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

calendar = client.econ.calendar(
    from_date='2025-01-01',
    to_date='2025-01-02',
    country='US,AU',
    min_importance=0,
    currency='USD',
    category='employment'
)
print(calendar)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const calendar = await client.econ.calendar({
  from: '2025-01-01',
  to: '2025-01-02',
  country: 'US,AU',
  minImportance: 0,
  currency: 'USD',
  category: 'employment',
  limit: 10
});
console.log(calendar);`,
  },


  // news
  'news/ticker': {
    http: `GET https://api.axionquant.com/news/AAPL`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

news = client.news.company('AAPL')
print(news)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const news = await client.news.company('AAPL');
console.log(news);`,
  },
  'news/country': {
    http: `GET https://api.axionquant.com/news/country/US`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

news = client.news.country('US')
print(news)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const news = await client.news.country('US');
console.log(news);`,
  },
  'news/category': {
    http: `GET https://api.axionquant.com/news/category/technology`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

news = client.news.category('technology')
print(news)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const news = await client.news.category('technology');
console.log(news);`,
  },
  'news/general': {
    http: `GET https://api.axionquant.com/news`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

news = client.news.general()
print(news)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const news = await client.news.general();
console.log(news);`,
  },

  //webtraffic
  'stocks/traffic': {
    http: `GET https://api.axionquant.com/web-traffic/AAPL/traffic`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

traffic = client.web_traffic.traffic('AAPL')
print(traffic)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const traffic = await client.webTraffic.traffic('AAPL');
console.log(traffic);`,
  },


  'profile/ticker': {
    http: `GET https://api.axionquant.com/profiles/AAPL`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

profile = client.profiles.profile('AAPL')
print(profile)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const profile = await client.profiles.profile('AAPL');
console.log(profile);`,
  },
  'profile/ticker/info': {
    http: `GET https://api.axionquant.com/profiles/AAPL/info`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

info = client.profiles.info('AAPL')
print(info)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const info = await client.profiles.info('AAPL');
console.log(info);`,
  },
  'profile/ticker/summary': {
    http: `GET https://api.axionquant.com/profiles/AAPL/summary`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

summary = client.profiles.summary('AAPL')
print(summary)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const summary = await client.profiles.summary('AAPL');
console.log(summary);`,
  },
  'profile/ticker/statistics': {
    http: `GET https://api.axionquant.com/profiles/AAPL/statistics`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

statistics = client.profiles.statistics('AAPL')
print(statistics)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const statistics = await client.profiles.statistics('AAPL');
console.log(statistics);`,
  },
  'profile/ticker/recommendation': {
    http: `GET https://api.axionquant.com/profiles/AAPL/recommendation`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

recommendation = client.profiles.recommendation('AAPL')
print(recommendation)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const recommendation = await client.profiles.recommendation('AAPL');
console.log(recommendation);`,
  },
  'profile/ticker/calendar': {
    http: `GET https://api.axionquant.com/profiles/AAPL/calendar`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

calendar = client.profiles.calendar('AAPL')
print(calendar)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const calendar = await client.profiles.calendar('AAPL');
console.log(calendar);`,
  },


  'filings/ticker/forms': {
    http: 'GET https://api.axionquant.com/filings/AAPL/4?startDate=2024-01-01&endDate=2024-03-31',
    python: `from axion import Axion
client = Axion(api_key='axn_123')

forms = client.filings.history(
    'AAPL',
    '4',
    start_date='2024-01-01',
    end_date='2024-03-31'
)
print(forms)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const forms = await client.filings.history('AAPL', '4', {
    startDate: '2024-01-01',
    endDate: '2024-03-31'
});
console.log(forms);`,
  },
  'filings/list/forms': {
    http: 'GET https://api.axionquant.com/filings/list/forms',
    python: `from axion import Axion
client = Axion(api_key='axn_123')

forms = client.filings.list_forms()
print(forms)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const forms = await client.filings.listForms();
console.log(forms);`,
  },
  'filings/search': {
    http: 'GET https://api.axionquant.com/filings/search?year=2024&quarter=1&form=4&ticker=AAPL',
    python: `from axion import Axion
client = Axion(api_key='axn_123')

results = client.filings.search(
    ticker='AAPL',
    form='4',
    year='2024',
    quarter='1'
)
print(results)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const results = await client.filings.search({
  ticker: 'AAPL',
  form: '4',
  year: '2024',
  quarter: '1'
});
console.log(results);`,
  },
  'filings/ticker': {
    http: 'GET https://api.axionquant.com/filings/AAPL?limit=10&form=10-K',
    python: `from axion import Axion
client = Axion(api_key='axn_123')

filings = client.filings.recent(
    'AAPL',
    form='10-K',
    limit=10
)
print(filings)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const filings = await client.filings.recent('AAPL', {
    form: '10-K',
    limit: 10
});
console.log(filings);`,
  },
  'filings/document/text': {
    http: 'GET https://api.axionquant.com/filings/document/text?documentId=aHR0cHM6Ly93d3cuc2VjLmdvdi9BcmNoaXZlcy9lZGdhci9kYXRhLzMyMDE5My8wMDAwMzIwMTkzMjYwMDAwMTMvYWFwbC0yMDI2MDMyOC5odG0=',
    python: `from axion import Axion
client = Axion(api_key='axn_123')

document_id = "aHR0cHM6Ly93d3cuc2VjLmdvdi9BcmNoaXZlcy9lZGdhci9kYXRhLzMyMDE5My8wMDAwMzIwMTkzMjYwMDAwMTMvYWFwbC0yMDI2MDMyOC5odG0="
text = client.filings.document_text(document_id)
print(text)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const documentId = "aHR0cHM6Ly93d3cuc2VjLmdvdi9BcmNoaXZlcy9lZGdhci9kYXRhLzMyMDE5My8wMDAwMzIwMTkzMjYwMDAwMTMvYWFwbC0yMDI2MDMyOC5odG0=";
const text = await client.filings.documentText(documentId);
console.log(text);`,
  },
  'filings/document/sentiment': {
    http: 'GET https://api.axionquant.com/filings/document/sentiment?documentId=aHR0cHM6Ly93d3cuc2VjLmdvdi9BcmNoaXZlcy9lZGdhci9kYXRhLzMyMDE5My8wMDAwMzIwMTkzMjYwMDAwMTMvYWFwbC0yMDI2MDMyOC5odG0=',
    python: `from axion import Axion
client = Axion(api_key='axn_123')

document_id = "aHR0cHM6Ly93d3cuc2VjLmdvdi9BcmNoaXZlcy9lZGdhci9kYXRhLzMyMDE5My8wMDAwMzIwMTkzMjYwMDAwMTMvYWFwbC0yMDI2MDMyOC5odG0="
sentiment = client.filings.document_sentiment(document_id)
print(sentiment)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const documentId = "aHR0cHM6Ly93d3cuc2VjLmdvdi9BcmNoaXZlcy9lZGdhci9kYXRhLzMyMDE5My8wMDAwMzIwMTkzMjYwMDAwMTMvYWFwbC0yMDI2MDMyOC5odG0=";
const sentiment = await client.filings.documentSentiment(documentId);
console.log(sentiment);`,
  },


  'financials/ticker/revenue': {
    http: 'GET https://api.axionquant.com/financials/AAPL/revenue?periods=4',
    python: `from axion import Axion
client = Axion(api_key='axn_123')

revenue = client.financials.revenue('AAPL', periods=4)
print(revenue)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const revenue = await client.financials.revenue('AAPL', {periods: 4});
console.log(revenue);`,
  },
  'financials/ticker/netincome': {
    http: 'GET https://api.axionquant.com/financials/AAPL/netincome?periods=4',
    python: `from axion import Axion
client = Axion(api_key='axn_123')

net_income = client.financials.net_income('AAPL', periods=4)
print(net_income)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const netIncome = await client.financials.netIncome('AAPL', {periods: 4});
console.log(netIncome);`,
  },
  'financials/ticker/total/assets': {
    http: 'GET https://api.axionquant.com/financials/AAPL/total/assets?periods=4',
    python: `from axion import Axion
client = Axion(api_key='axn_123')

total_assets = client.financials.total_assets('AAPL', periods=4)
print(total_assets)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const totalAssets = await client.financials.totalAssets('AAPL', {periods: 4});
console.log(totalAssets);`,
  },
  'financials/ticker/total/liabilities': {
    http: 'GET https://api.axionquant.com/financials/AAPL/total/liabilities?periods=4',
    python: `from axion import Axion
client = Axion(api_key='axn_123')

total_liabilities = client.financials.total_liabilities('AAPL', periods=4)
print(total_liabilities)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const totalLiabilities = await client.financials.totalLiabilities('AAPL', {periods: 4});
console.log(totalLiabilities);`,
  },
  'financials/ticker/stockholdersequity': {
    http: 'GET https://api.axionquant.com/financials/AAPL/stockholdersequity?periods=4',
    python: `from axion import Axion
client = Axion(api_key='axn_123')

stockholders_equity = client.financials.stockholders_equity('AAPL', periods=4)
print(stockholders_equity)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const stockholdersEquity = await client.financials.stockholdersEquity('AAPL', {periods: 4});
console.log(stockholdersEquity);`,
  },
  'financials/ticker/current/assets': {
    http: 'GET https://api.axionquant.com/financials/AAPL/current/assets?periods=4',
    python: `from axion import Axion
client = Axion(api_key='axn_123')

current_assets = client.financials.current_assets('AAPL', periods=4)
print(current_assets)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const currentAssets = await client.financials.currentAssets('AAPL', {periods: 4});
console.log(currentAssets);`,
  },
  'financials/ticker/current/liabilities': {
    http: 'GET https://api.axionquant.com/financials/AAPL/current/liabilities?periods=4',
    python: `from axion import Axion
client = Axion(api_key='axn_123')

current_liabilities = client.financials.current_liabilities('AAPL', periods=4)
print(current_liabilities)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const currentLiabilities = await client.financials.currentLiabilities('AAPL', {periods: 4});
console.log(currentLiabilities);`,
  },
  'financials/ticker/cashflow/operating': {
    http: 'GET https://api.axionquant.com/financials/AAPL/cashflow/operating?periods=4',
    python: `from axion import Axion
client = Axion(api_key='axn_123')

operating_cash_flow = client.financials.operating_cash_flow('AAPL', periods=4)
print(operating_cash_flow)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const operatingCashFlow = await client.financials.operatingCashFlow('AAPL', {periods: 4});
console.log(operatingCashFlow);`,
  },
  'financials/ticker/capitalexpenditures': {
    http: 'GET https://api.axionquant.com/financials/AAPL/capitalexpenditures?periods=4',
    python: `from axion import Axion
client = Axion(api_key='axn_123')

capital_expenditures = client.financials.capital_expenditures('AAPL', periods=4)
print(capital_expenditures)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const capitalExpenditures = await client.financials.capitalExpenditures('AAPL', {periods: 4});
console.log(capitalExpenditures);`,
  },
  'financials/ticker/cashflow/free': {
    http: 'GET https://api.axionquant.com/financials/AAPL/cashflow/free?periods=4',
    python: `from axion import Axion
client = Axion(api_key='axn_123')

free_cash_flow = client.financials.free_cash_flow('AAPL', periods=4)
print(free_cash_flow)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const freeCashFlow = await client.financials.freeCashFlow('AAPL', {periods: 4});
console.log(freeCashFlow);`,
  },
  'financials/ticker/sharesoutstanding/basic': {
    http: 'GET https://api.axionquant.com/financials/AAPL/sharesoutstanding/basic?periods=4',
    python: `from axion import Axion
client = Axion(api_key='axn_123')

shares_outstanding_basic = client.financials.shares_outstanding_basic('AAPL', periods=4)
print(shares_outstanding_basic)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const sharesOutstandingBasic = await client.financials.sharesOutstandingBasic('AAPL', {periods: 4});
console.log(sharesOutstandingBasic);`,
  },
  'financials/ticker/sharesoutstanding/diluted': {
    http: 'GET https://api.axionquant.com/financials/AAPL/sharesoutstanding/diluted?periods=4',
    python: `from axion import Axion
client = Axion(api_key='axn_123')

shares_outstanding_diluted = client.financials.shares_outstanding_diluted('AAPL', periods=4)
print(shares_outstanding_diluted)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const sharesOutstandingDiluted = await client.financials.sharesOutstandingDiluted('AAPL', {periods: 4});
console.log(sharesOutstandingDiluted);`,
  },
  'financials/ticker/metrics': {
    http: 'GET https://api.axionquant.com/financials/AAPL/metrics',
    python: `from axion import Axion
client = Axion(api_key='axn_123')

metrics = client.financials.metrics('AAPL')
print(metrics)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const metrics = await client.financials.metrics('AAPL');
console.log(metrics);`,
  },
  'financials/ticker/snapshot': {
    http: 'GET https://api.axionquant.com/financials/AAPL/snapshot',
    python: `from axion import Axion
client = Axion(api_key='axn_123')

snapshot = client.financials.snapshot('AAPL')
print(snapshot)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const snapshot = await client.financials.snapshot('AAPL');
console.log(snapshot);`,
  },
  'financials/statements/balance': {
    http: 'GET https://api.axionquant.com/financials/statements/AAPL/balance?year=2024&quarter=1',
    python: `from axion import Axion

client = Axion(api_key='axn_123')

balance = client.financials.balance_sheet(
    'AAPL',
    year='2024',
    quarter='1',
)
print(balance)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const balance = await client.financials.balanceSheet(
    'AAPL',
    { year: '2024', quarter: '1' },
);
console.log(balance);`,
  },
  'financials/statements/income': {
    http: 'GET https://api.axionquant.com/financials/statements/AAPL/income?year=2024&quarter=1',
    python: `from axion import Axion

client = Axion(api_key='axn_123')

income = client.financials.income_statement(
    'AAPL',
    year='2024',
    quarter='1',
)
print(income)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const income = await client.financials.incomeStatement(
    'AAPL',
    { year: '2024', quarter: '1' },
);
console.log(income);`,
  },
  'financials/statements/cashflow': {
    http: 'GET https://api.axionquant.com/financials/statements/AAPL/cashflow?year=2024&quarter=1',
    python: `from axion import Axion

client = Axion(api_key='axn_123')

cashflow = client.financials.cash_flow_statement(
    'AAPL',
    year='2024',
    quarter='1',
)
print(cashflow)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const cashflow = await client.financials.cashFlowStatement(
    'AAPL',
    { year: '2024', quarter: '1' },
);
console.log(cashflow);`,
  },
  'financials/dcf/value': {
    http: 'GET https://api.axionquant.com/financials/dcf/AAPL/value',
    python: `from axion import Axion
client = Axion(api_key='axn_123')

dcf = client.financials.dcf_value('AAPL')
print(dcf)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const dcf = await client.financials.dcfValue('AAPL');
console.log(dcf);`,
  },
  'financials/dcf/rate': {
    http: 'GET https://api.axionquant.com/financials/dcf/AAPL/rate',
    python: `from axion import Axion
client = Axion(api_key='axn_123')

rate = client.financials.dcf_rate('AAPL')
print(rate)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const rate = await client.financials.dcfRate('AAPL');
console.log(rate);`,
  },

  'financials/ticker/eps': {
    http: 'GET https://api.axionquant.com/financials/AAPL/eps?from=2024-01-01&to=2024-12-31',
    python: `from axion import Axion
client = Axion(api_key='axn_123')

eps = client.financials.eps('AAPL',
    from_date='2024-01-01',
    to_date='2024-12-31'
)
print(eps)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const eps = await client.financials.eps('AAPL', {
  from: '2024-01-01',
  to: '2024-12-31'
});
console.log(eps);`,
  },
  'financials/ticker/pe': {
    http: 'GET https://api.axionquant.com/financials/AAPL/pe?from=2024-01-01&to=2024-12-31&frame=weekly',
    python: `from axion import Axion
client = Axion(api_key='axn_123')

pe = client.financials.pe('AAPL',
    from_date='2024-01-01',
    to_date='2024-12-31',
    frame='weekly'
)
print(pe)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const pe = await client.financials.pe('AAPL', {
  from: '2024-01-01',
  to: '2024-12-31',
  frame: 'weekly'
});
console.log(pe);`,
  },
  'financials/ticker/marketcap': {
    http: 'GET https://api.axionquant.com/financials/AAPL/marketcap?from=2024-01-01&to=2024-12-31&frame=monthly',
    python: `from axion import Axion
client = Axion(api_key='axn_123')

market_cap = client.financials.market_cap('AAPL',
    from_date='2024-01-01',
    to_date='2024-12-31',
    frame='monthly'
)
print(market_cap)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const marketCap = await client.financials.marketCap('AAPL', {
  from: '2024-01-01',
  to: '2024-12-31',
  frame: 'monthly'
});
console.log(marketCap);`,
  },
  'financials/ticker/roe': {
    http: 'GET https://api.axionquant.com/financials/AAPL/roe?from=2024-01-01&to=2024-12-31',
    python: `from axion import Axion
client = Axion(api_key='axn_123')

roe = client.financials.roe('AAPL',
    from_date='2024-01-01',
    to_date='2024-12-31'
)
print(roe)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const roe = await client.financials.roe('AAPL', {
  from: '2024-01-01',
  to: '2024-12-31'
});
console.log(roe);`,
  },
  'financials/ticker/ev': {
    http: 'GET https://api.axionquant.com/financials/AAPL/ev?from=2024-01-01&to=2024-12-31&frame=weekly',
    python: `from axion import Axion
client = Axion(api_key='axn_123')

ev = client.financials.enterprise_value('AAPL',
    from_date='2024-01-01',
    to_date='2024-12-31',
    frame='weekly'
)
print(ev)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const ev = await client.financials.enterpriseValue('AAPL', {
  from: '2024-01-01',
  to: '2024-12-31',
  frame: 'weekly'
});
console.log(ev);`,
  },
  'financials/ticker/ebitda': {
    http: 'GET https://api.axionquant.com/financials/AAPL/ebitda?from=2024-01-01&to=2024-12-31',
    python: `from axion import Axion
client = Axion(api_key='axn_123')

ebitda = client.financials.ebitda('AAPL',
    from_date='2024-01-01',
    to_date='2024-12-31'
)
print(ebitda)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const ebitda = await client.financials.ebitda('AAPL', {
  from: '2024-01-01',
  to: '2024-12-31'
});
console.log(ebitda);`,
  },
  'financials/ticker/de': {
    http: 'GET https://api.axionquant.com/financials/AAPL/de?from=2024-01-01&to=2024-12-31',
    python: `from axion import Axion
client = Axion(api_key='axn_123')

de = client.financials.debt_to_equity('AAPL',
    from_date='2024-01-01',
    to_date='2024-12-31'
)
print(de)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const de = await client.financials.debtToEquity('AAPL', {
  from: '2024-01-01',
  to: '2024-12-31'
});
console.log(de);`,
  },

  'insiders/ticker/funds': {
    http: 'GET https://api.axionquant.com/insiders/AAPL/funds',
    python: `from axion import Axion
client = Axion(api_key='axn_123')

funds = client.insiders.funds('AAPL')
print(funds)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const funds = await client.insiders.funds('AAPL');
console.log(funds);`,
  },
  'insiders/ticker/individuals': {
    http: 'GET https://api.axionquant.com/insiders/AAPL/individuals',
    python: `from axion import Axion
client = Axion(api_key='axn_123')

individuals = client.insiders.individuals('AAPL')
print(individuals)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const individuals = await client.insiders.individuals('AAPL');
console.log(individuals);`,
  },
  'insiders/ticker/institutions': {
    http: 'GET https://api.axionquant.com/insiders/AAPL/institutions',
    python: `from axion import Axion
client = Axion(api_key='axn_123')

institutions = client.insiders.institutions('AAPL')
print(institutions)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const institutions = await client.insiders.institutions('AAPL');
console.log(institutions);`,
  },
  'insiders/ticker/ownership': {
    http: 'GET https://api.axionquant.com/insiders/AAPL/ownership',
    python: `from axion import Axion
client = Axion(api_key='axn_123')

ownership = client.insiders.ownership('AAPL')
print(ownership)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const ownership = await client.insiders.ownership('AAPL');
console.log(ownership);`,
  },
  'insiders/ticker/activity': {
    http: 'GET https://api.axionquant.com/insiders/AAPL/activity',
    python: `from axion import Axion
client = Axion(api_key='axn_123')

activity = client.insiders.activity('AAPL')
print(activity)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const activity = await client.insiders.activity('AAPL');
console.log(activity);`,
  },
  'insiders/ticker/transactions': {
    http: 'GET https://api.axionquant.com/insiders/AAPL/transactions',
    python: `from axion import Axion
client = Axion(api_key='axn_123')

transactions = client.insiders.transactions('AAPL')
print(transactions)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const transactions = await client.insiders.transactions('AAPL');
console.log(transactions);`,
  },

  'earnings/ticker/history': {
    http: 'GET https://api.axionquant.com/earnings/AAPL/history',
    python: `from axion import Axion
client = Axion(api_key='axn_123')

history = client.earnings.history('AAPL')
print(history)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const history = await client.earnings.history('AAPL');
console.log(history);`,
  },
  'earnings/ticker/trend': {
    http: 'GET https://api.axionquant.com/earnings/AAPL/trend',
    python: `from axion import Axion
client = Axion(api_key='axn_123')

trend = client.earnings.trend('AAPL')
print(trend)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const trend = await client.earnings.trend('AAPL');
console.log(trend);`,
  },
  'earnings/ticker/index': {
    http: 'GET https://api.axionquant.com/earnings/AAPL/index',
    python: `from axion import Axion
client = Axion(api_key='axn_123')

index = client.earnings.index('AAPL')
print(index)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const index = await client.earnings.index('AAPL');
console.log(index);`,
  },
  'earnings/ticker/report': {
    http: 'GET https://api.axionquant.com/earnings/AAPL/report?year=2023&quarter=4',
    python: `from axion import Axion
client = Axion(api_key='axn_123')

report = client.earnings.report('AAPL',
    year='2023',
    quarter='4'
)
print(report)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const report = await client.earnings.report('AAPL', {
  year: '2023',
  quarter: '4'
});
console.log(report);`,
  },

  'earnings/ticker/transcript': {
    http: 'GET https://api.axionquant.com/earnings/AAPL/transcript?year=2024&quarter=2',
    python: `from axion import Axion
client = Axion(api_key='axn_123')

transcript = client.earnings.transcript('AAPL',
    year='2024',
    quarter='2'
)
print(transcript)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const transcript = await client.earnings.transcript('AAPL', {
  year: '2024',
  quarter: '2'
});
console.log(transcript);`,
  },
  'earnings/transcript/sentiment': {
    http: 'GET https://api.axionquant.com/earnings/transcript/sentiment?id=<base64>',
    python: `from axion import Axion
client = Axion(api_key='axn_123')

encoded_id = "eyJ0aWNrZXIiOiJBQVBMIiwieWVhciI6IjIwMjQiLCJxdWFydGVyIjoiMiJ9"
sentiment = client.earnings.transcript_sentiment(encoded_id)
print(sentiment)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const encodedId = "eyJ0aWNrZXIiOiJBQVBMIiwieWVhciI6IjIwMjQiLCJxdWFydGVyIjoiMiJ9";
const sentiment = await client.earnings.transcriptSentiment(encodedId);
console.log(sentiment);`,
  },

  // stocks list
  'stocks.list(market)': {
    http: `GET https://api.axionquant.com/stocks/list/market`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

values = client.stocks.list('market')
print(values)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const values = await client.stocks.list('market');
console.log(values);`,
  },
  'stocks.list(country)': {
    http: `GET https://api.axionquant.com/stocks/list/country`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

values = client.stocks.list('country')
print(values)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const values = await client.stocks.list('country');
console.log(values);`,
  },
  'stocks.list(currency)': {
    http: `GET https://api.axionquant.com/stocks/list/currency`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

values = client.stocks.list('currency')
print(values)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const values = await client.stocks.list('currency');
console.log(values);`,
  },
  'stocks.list(sector)': {
    http: `GET https://api.axionquant.com/stocks/list/sector`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

values = client.stocks.list('sector')
print(values)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const values = await client.stocks.list('sector');
console.log(values);`,
  },
  'stocks.list(industry)': {
    http: `GET https://api.axionquant.com/stocks/list/industry`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

values = client.stocks.list('industry')
print(values)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const values = await client.stocks.list('industry');
console.log(values);`,
  },
  'stocks.list(type)': {
    http: `GET https://api.axionquant.com/stocks/list/type`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

values = client.stocks.list('type')
print(values)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const values = await client.stocks.list('type');
console.log(values);`,
  },
  'stocks.list(exchange)': {
    http: `GET https://api.axionquant.com/stocks/list/exchange`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

values = client.stocks.list('exchange')
print(values)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const values = await client.stocks.list('exchange');
console.log(values);`,
  },

  // etfs list
  'etfs.list(market)': {
    http: `GET https://api.axionquant.com/etfs/list/market`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

values = client.etfs.list('market')
print(values)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const values = await client.etfs.list('market');
console.log(values);`,
  },
  'etfs.list(country)': {
    http: `GET https://api.axionquant.com/etfs/list/country`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

values = client.etfs.list('country')
print(values)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const values = await client.etfs.list('country');
console.log(values);`,
  },
  'etfs.list(currency)': {
    http: `GET https://api.axionquant.com/etfs/list/currency`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

values = client.etfs.list('currency')
print(values)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const values = await client.etfs.list('currency');
console.log(values);`,
  },
  'etfs.list(sector)': {
    http: `GET https://api.axionquant.com/etfs/list/sector`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

values = client.etfs.list('sector')
print(values)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const values = await client.etfs.list('sector');
console.log(values);`,
  },
  'etfs.list(industry)': {
    http: `GET https://api.axionquant.com/etfs/list/industry`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

values = client.etfs.list('industry')
print(values)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const values = await client.etfs.list('industry');
console.log(values);`,
  },
  'etfs.list(type)': {
    http: `GET https://api.axionquant.com/etfs/list/type`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

values = client.etfs.list('type')
print(values)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const values = await client.etfs.list('type');
console.log(values);`,
  },
  'etfs.list(exchange)': {
    http: `GET https://api.axionquant.com/etfs/list/exchange`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

values = client.etfs.list('exchange')
print(values)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const values = await client.etfs.list('exchange');
console.log(values);`,
  },

  // indices list
  'indices.list(exchange)': {
    http: `GET https://api.axionquant.com/indices/list/exchange`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

values = client.indices.list('exchange')
print(values)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const values = await client.indices.list('exchange');
console.log(values);`,
  },
  'indices.list(timezone)': {
    http: `GET https://api.axionquant.com/indices/list/timezone`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

values = client.indices.list('timezone')
print(values)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const values = await client.indices.list('timezone');
console.log(values);`,
  },
  'indices.list(country)': {
    http: `GET https://api.axionquant.com/indices/list/country`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

values = client.indices.list('country')
print(values)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const values = await client.indices.list('country');
console.log(values);`,
  },

  // futures list
  'futures.list(exchange)': {
    http: `GET https://api.axionquant.com/futures/list/exchange`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

values = client.futures.list('exchange')
print(values)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const values = await client.futures.list('exchange');
console.log(values);`,
  },
  'futures.list(currency)': {
    http: `GET https://api.axionquant.com/futures/list/currency`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

values = client.futures.list('currency')
print(values)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const values = await client.futures.list('currency');
console.log(values);`,
  },
  'futures.list(timezone)': {
    http: `GET https://api.axionquant.com/futures/list/timezone`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

values = client.futures.list('timezone')
print(values)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const values = await client.futures.list('timezone');
console.log(values);`,
  },
  'futures.list(country)': {
    http: `GET https://api.axionquant.com/futures/list/country`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

values = client.futures.list('country')
print(values)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const values = await client.futures.list('country');
console.log(values);`,
  },

  // forex list
  'forex.list(exchange)': {
    http: `GET https://api.axionquant.com/forex/list/exchange`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

values = client.forex.list('exchange')
print(values)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const values = await client.forex.list('exchange');
console.log(values);`,
  },
  'forex.list(rating)': {
    http: `GET https://api.axionquant.com/forex/list/rating`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

values = client.forex.list('rating')
print(values)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const values = await client.forex.list('rating');
console.log(values);`,
  },
  'forex.list(country)': {
    http: `GET https://api.axionquant.com/forex/list/country`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

values = client.forex.list('country')
print(values)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const values = await client.forex.list('country');
console.log(values);`,
  },

  // crypto list
  'crypto.list(category)': {
    http: `GET https://api.axionquant.com/crypto/list/category`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

values = client.crypto.list('category')
print(values)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const values = await client.crypto.list('category');
console.log(values);`,
  },
  'crypto.list(rating)': {
    http: `GET https://api.axionquant.com/crypto/list/rating`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

values = client.crypto.list('rating')
print(values)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const values = await client.crypto.list('rating');
console.log(values);`,
  },
  'crypto.list(type)': {
    http: `GET https://api.axionquant.com/crypto/list/type`,
    python: `from axion import Axion
client = Axion(api_key='axn_123')

values = client.crypto.list('type')
print(values)`,
    javascript: `const { Axion } = require('@axionquant/sdk');
const API_KEY = 'axn_123';
const client = new Axion(API_KEY);

const values = await client.crypto.list('type');
console.log(values);`,
  },
}

// ─── ROUTES_META ──────────────────────────────────────────────────────────────
// Route metadata keyed by CLIENT_CODE IDs for dynamic route building.
// Add a new entry here + code in CLIENT_CODE + response in RESPONSES.

export const ROUTES_META: Record<string, RouteMeta> = {
  'stocks/tickers': { responseKey: '01-market-data/stocks/find-tickers', method: 'GET', label: 'List Tickers', path: '/stocks/tickers', category: 'Stocks', description: 'Returns all available stock tickers, optionally filtered by exchange.', params: [{ name: 'country', required: false, type: 'string', location: 'query', description: 'Market country', defaultValue: 'america' }, { name: 'exchange', required: false, type: 'string', location: 'query', description: 'e.g. NASDAQ', defaultValue: 'NASDAQ' }] },
  'stocks/ticker': { responseKey: '01-market-data/stocks/ticker-details', method: 'GET', label: 'Get Ticker', path: '/stocks/{ticker}', category: 'Stocks', description: 'Returns details for a single stock ticker.', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', description: 'Stock symbol', defaultValue: 'AAPL' }] },
  'stocks/ticker-quote': { responseKey: '01-market-data/stocks/quote', method: 'GET', label: 'Get Quote', path: '/stocks/{ticker}/quote', category: 'Stocks', description: 'Returns the latest quote data for a stock.', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', description: 'Stock symbol', defaultValue: 'AAPL' }] },
  'stocks/prices': { responseKey: '01-market-data/stocks/historical-prices', method: 'GET', label: 'Historical Prices', path: '/stocks/{ticker}/prices', category: 'Stocks', description: 'Returns OHLCV price history for a stock.', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'AAPL' }, { name: 'frame', required: false, type: 'string', location: 'query', description: 'daily | weekly | monthly', defaultValue: 'daily' }, { name: 'from', required: false, type: 'string', location: 'query', description: 'YYYY-MM-DD', defaultValue: '2025-12-01' }, { name: 'to', required: false, type: 'string', location: 'query', description: 'YYYY-MM-DD', defaultValue: '2025-12-19' }] },
  'stocks/gainers': { responseKey: '01-market-data/stocks/gainers', method: 'GET', label: 'Top Gainers', path: '/stocks/gainers', category: 'Stocks', params: [{ name: 'market', required: false, type: 'string', location: 'query', description: 'Market country', defaultValue: 'america' }, { name: 'days', required: false, type: 'number', location: 'query', defaultValue: '5' }, { name: 'limit', required: false, type: 'number', location: 'query', defaultValue: '10' }] },
  'stocks/losers': { responseKey: '01-market-data/stocks/losers', method: 'GET', label: 'Top Losers', path: '/stocks/losers', category: 'Stocks', params: [{ name: 'market', required: false, type: 'string', location: 'query', description: 'Market country', defaultValue: 'america' }, { name: 'days', required: false, type: 'number', location: 'query', defaultValue: '5' }, { name: 'limit', required: false, type: 'number', location: 'query', defaultValue: '10' }] },
  'crypto/tickers': { responseKey: '01-market-data/crypto/find-tickers', method: 'GET', label: 'List Tickers', path: '/crypto/tickers', category: 'Crypto', params: [{ name: 'type', required: false, type: 'string', location: 'query', description: 'spot | futures', defaultValue: 'spot' }] },
  'crypto/ticker': { responseKey: '01-market-data/crypto/ticker-details', method: 'GET', label: 'Get Ticker', path: '/crypto/{ticker}', category: 'Crypto', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'BTC' }] },
  'crypto/ticker-quote': { responseKey: '01-market-data/crypto/quote', method: 'GET', label: 'Get Quote', path: '/crypto/{ticker}/quote', category: 'Crypto', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'BTC' }] },
  'crypto/prices': { responseKey: '01-market-data/crypto/historical-prices', method: 'GET', label: 'Historical Prices', path: '/crypto/{ticker}/prices', category: 'Crypto', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'BTC' }, { name: 'frame', required: false, type: 'string', location: 'query', defaultValue: 'daily' }, { name: 'from', required: false, type: 'string', location: 'query', defaultValue: '2025-12-01' }, { name: 'to', required: false, type: 'string', location: 'query', defaultValue: '2025-12-19' }] },
  'crypto/gainers': { responseKey: '01-market-data/crypto/gainers', method: 'GET', label: 'Top Gainers', path: '/crypto/gainers', category: 'Crypto', params: [{ name: 'days', required: false, type: 'number', location: 'query', defaultValue: '5' }, { name: 'limit', required: false, type: 'number', location: 'query', defaultValue: '10' }] },
  'crypto/losers': { responseKey: '01-market-data/crypto/losers', method: 'GET', label: 'Top Losers', path: '/crypto/losers', category: 'Crypto', params: [{ name: 'days', required: false, type: 'number', location: 'query', defaultValue: '5' }, { name: 'limit', required: false, type: 'number', location: 'query', defaultValue: '10' }] },
  'etfs/tickers': { responseKey: '01-market-data/ETFs/find-tickers', method: 'GET', label: 'List Tickers', path: '/etfs/tickers', category: 'ETFs', params: [{ name: 'country', required: false, type: 'string', location: 'query', description: 'Market country', defaultValue: 'america' }, { name: 'exchange', required: false, type: 'string', location: 'query', defaultValue: 'NASDAQ' }] },
  'etfs/ticker': { responseKey: '01-market-data/ETFs/ticker-details', method: 'GET', label: 'Get Ticker', path: '/etfs/{ticker}', category: 'ETFs', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'SPY' }] },
  'etfs/fund': { responseKey: '01-market-data/ETFs/fund-details', method: 'GET', label: 'Fund Details', path: '/etfs/{ticker}/fund', category: 'ETFs', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'SPY' }] },
  'etfs/ticker-quote': { responseKey: '01-market-data/ETFs/quote', method: 'GET', label: 'Get Quote', path: '/etfs/{ticker}/quote', category: 'ETFs', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'SPY' }] },
  'etfs/prices': { responseKey: '01-market-data/ETFs/historical-prices', method: 'GET', label: 'Historical Prices', path: '/etfs/{ticker}/prices', category: 'ETFs', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'SPY' }, { name: 'frame', required: false, type: 'string', location: 'query', defaultValue: 'daily' }, { name: 'from', required: false, type: 'string', location: 'query', defaultValue: '2025-12-01' }, { name: 'to', required: false, type: 'string', location: 'query', defaultValue: '2025-12-19' }] },
  'etfs/holdings': { responseKey: '01-market-data/ETFs/holdings', method: 'GET', label: 'Holdings', path: '/etfs/{ticker}/holdings', category: 'ETFs', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'SPY' }] },
  'etfs/holdings-all': { responseKey: '01-market-data/ETFs/holdings-all', method: 'GET', label: 'All Holdings', path: '/etfs/{ticker}/holdings/all', category: 'ETFs', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'SPY' }] },
  'etfs/weights': { responseKey: '01-market-data/ETFs/weights', method: 'GET', label: 'Weights', path: '/etfs/{ticker}/weights', category: 'ETFs', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'SPY' }] },
  'etfs/exposure': { responseKey: '01-market-data/ETFs/exposure', method: 'GET', label: 'ETF Exposure', path: '/etfs/{ticker}/exposure', category: 'ETFs', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'AAPL' }] },
  'etfs/gainers': { responseKey: '01-market-data/ETFs/gainers', method: 'GET', label: 'Top Gainers', path: '/etfs/gainers', category: 'ETFs', params: [{ name: 'days', required: false, type: 'number', location: 'query', defaultValue: '5' }, { name: 'limit', required: false, type: 'number', location: 'query', defaultValue: '10' }] },
  'etfs/losers': { responseKey: '01-market-data/ETFs/losers', method: 'GET', label: 'Top Losers', path: '/etfs/losers', category: 'ETFs', params: [{ name: 'days', required: false, type: 'number', location: 'query', defaultValue: '5' }, { name: 'limit', required: false, type: 'number', location: 'query', defaultValue: '10' }] },
  'forex/tickers': { responseKey: '01-market-data/forex/find-tickers', method: 'GET', label: 'List Tickers', path: '/forex/tickers', category: 'Forex', params: [{ name: 'country', required: false, type: 'string', location: 'query', defaultValue: 'AE' }, { name: 'exchange', required: false, type: 'string', location: 'query', description: 'Exchange code', defaultValue: 'IDC' }] },
  'forex/ticker': { responseKey: '01-market-data/forex/ticker-details', method: 'GET', label: 'Get Ticker', path: '/forex/{ticker}', category: 'Forex', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'AEDAUD' }] },
  'forex/ticker-quote': { responseKey: '01-market-data/forex/quote', method: 'GET', label: 'Get Quote', path: '/forex/{ticker}/quote', category: 'Forex', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'EURUSD' }] },
  'forex/prices': { responseKey: '01-market-data/forex/historical-prices', method: 'GET', label: 'Historical Prices', path: '/forex/{ticker}/prices', category: 'Forex', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'AEDAUD' }, { name: 'frame', required: false, type: 'string', location: 'query', defaultValue: 'daily' }, { name: 'from', required: false, type: 'string', location: 'query', defaultValue: '2025-12-01' }, { name: 'to', required: false, type: 'string', location: 'query', defaultValue: '2025-12-19' }] },
  'indices/tickers': { responseKey: '01-market-data/indices/find-tickers', method: 'GET', label: 'List Tickers', path: '/indices/tickers', category: 'Indices', params: [{ name: 'exchange', required: false, type: 'string', location: 'query', defaultValue: 'ASX' }] },
  'indices/ticker': { responseKey: '01-market-data/indices/ticker-details', method: 'GET', label: 'Get Ticker', path: '/indices/{ticker}', category: 'Indices', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'SPX' }] },
  'indices/ticker-quote': { responseKey: '01-market-data/indices/quote', method: 'GET', label: 'Get Quote', path: '/indices/{ticker}/quote', category: 'Indices', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'SPX' }] },
  'indices/prices': { responseKey: '01-market-data/indices/historical-prices', method: 'GET', label: 'Historical Prices', path: '/indices/{ticker}/prices', category: 'Indices', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'GVZ' }, { name: 'frame', required: false, type: 'string', location: 'query', defaultValue: 'daily' }, { name: 'from', required: false, type: 'string', location: 'query', defaultValue: '2025-12-01' }, { name: 'to', required: false, type: 'string', location: 'query', defaultValue: '2025-12-19' }] },
  'futures/tickers': { responseKey: '01-market-data/futures/find-tickers', method: 'GET', label: 'List Tickers', path: '/futures/tickers', category: 'Futures', params: [{ name: 'exchange', required: false, type: 'string', location: 'query', defaultValue: 'CME' }] },
  'futures/ticker': { responseKey: '01-market-data/futures/ticker-details', method: 'GET', label: 'Get Ticker', path: '/futures/{ticker}', category: 'Futures', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'ALI' }] },
  'futures/ticker-quote': { responseKey: '01-market-data/futures/quote', method: 'GET', label: 'Get Quote', path: '/futures/{ticker}/quote', category: 'Futures', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'ES' }] },
  'futures/prices': { responseKey: '01-market-data/futures/historical-prices', method: 'GET', label: 'Historical Prices', path: '/futures/{ticker}/prices', category: 'Futures', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'ALI' }, { name: 'frame', required: false, type: 'string', location: 'query', defaultValue: 'daily' }, { name: 'from', required: false, type: 'string', location: 'query', defaultValue: '2025-12-01' }, { name: 'to', required: false, type: 'string', location: 'query', defaultValue: '2025-12-19' }] },
  'news/ticker': { responseKey: '04-macros-and-signals/news/corporate', method: 'GET', label: 'Company News', path: '/news/{ticker}', category: 'News', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'AAPL' }] },
  'news/country': { responseKey: '04-macros-and-signals/news/global', method: 'GET', label: 'Country News', path: '/news/country/{country}', category: 'News', params: [{ name: 'country', required: true, type: 'string', location: 'path', defaultValue: 'US' }] },
  'news/category': { responseKey: '04-macros-and-signals/news/corporate', method: 'GET', label: 'Category News', path: '/news/category/{category}', category: 'News', params: [{ name: 'category', required: true, type: 'string', location: 'path', defaultValue: 'technology' }] },
  'news/general': { responseKey: '04-macros-and-signals/news/live-feed', method: 'GET', label: 'Live Feed', path: '/news', category: 'News', params: [] },
  'news/article': { responseKey: '04-macros-and-signals/news/live-feed', method: 'GET', label: 'News Article', path: '/news/article/{id}', category: 'News', description: 'Redirects to the original news article URL.', params: [{ name: 'id', required: true, type: 'string', location: 'path', description: 'Base64-encoded article URL', defaultValue: 'aHR0cHM6Ly9leGFtcGxlLmNvbS9hcnRpY2xl' }] },
  'econ/find': { responseKey: '04-macros-and-signals/economic-datasets/find', method: 'GET', label: 'Find Datasets (AI)', path: '/econ/find', category: 'Economics', params: [{ name: 'query', required: true, type: 'string', location: 'query', defaultValue: 'Semiconductor spending' }] },
  'econ/search': { responseKey: '04-macros-and-signals/economic-datasets/00-search', method: 'GET', label: 'Search Datasets', path: '/econ/search', category: 'Economics', params: [{ name: 'query', required: true, type: 'string', location: 'query', defaultValue: 'Corn' }] },
  'econ/dataset': { responseKey: '04-macros-and-signals/economic-datasets/data', method: 'GET', label: 'Dataset Data', path: '/econ/dataset/{seriesId}', category: 'Economics', params: [{ name: 'seriesId', required: true, type: 'string', location: 'path', defaultValue: 'PMAIZMTUSDM' }] },
  'econ/calendar': { responseKey: '04-macros-and-signals/calendar/economic-calendar', method: 'GET', label: 'Economic Calendar', path: '/econ/calendar', category: 'Economics', params: [{ name: 'from', required: false, type: 'string', location: 'query', defaultValue: '2025-01-01' }, { name: 'to', required: false, type: 'string', location: 'query', defaultValue: '2025-01-02' }, { name: 'country', required: false, type: 'string', location: 'query', defaultValue: 'US,AU' }, { name: 'minImportance', required: false, type: 'number', location: 'query', defaultValue: '0' }, { name: 'currency', required: false, type: 'string', location: 'query', description: 'Filter by currency code(s), comma-separated', defaultValue: '' }, { name: 'category', required: false, type: 'string', location: 'query', description: 'Filter by category', defaultValue: '' }, { name: 'limit', required: false, type: 'number', location: 'query', defaultValue: '10' }] },
  'credit/search': { responseKey: '04-macros-and-signals/credit-ratings/00-search', method: 'GET', label: 'Search Entities', path: '/credit/search', category: 'Credit', params: [{ name: 'query', required: true, type: 'string', location: 'query', defaultValue: 'Apple' }] },
  'credit/ratings': { responseKey: '04-macros-and-signals/credit-ratings/report', method: 'GET', label: 'Credit Ratings', path: '/credit/ratings/{entityId}', category: 'Credit', params: [{ name: 'entityId', required: true, type: 'string', location: 'path', defaultValue: '112354' }] },
  'forex/gainers': { responseKey: '01-market-data/forex/gainers', method: 'GET', label: 'Top Gainers', path: '/forex/gainers', category: 'Forex', params: [{ name: 'days', required: false, type: 'number', location: 'query', defaultValue: '5' }, { name: 'limit', required: false, type: 'number', location: 'query', defaultValue: '10' }] },
  'forex/losers': { responseKey: '01-market-data/forex/losers', method: 'GET', label: 'Top Losers', path: '/forex/losers', category: 'Forex', params: [{ name: 'days', required: false, type: 'number', location: 'query', defaultValue: '5' }, { name: 'limit', required: false, type: 'number', location: 'query', defaultValue: '10' }] },
  'indices/gainers': { responseKey: '01-market-data/indices/gainers', method: 'GET', label: 'Top Gainers', path: '/indices/gainers', category: 'Indices', params: [{ name: 'days', required: false, type: 'number', location: 'query', defaultValue: '5' }, { name: 'limit', required: false, type: 'number', location: 'query', defaultValue: '10' }] },
  'indices/losers': { responseKey: '01-market-data/indices/losers', method: 'GET', label: 'Top Losers', path: '/indices/losers', category: 'Indices', params: [{ name: 'days', required: false, type: 'number', location: 'query', defaultValue: '5' }, { name: 'limit', required: false, type: 'number', location: 'query', defaultValue: '10' }] },
  'indices/components': { responseKey: '01-market-data/indices/components', method: 'GET', label: 'Index Components', path: '/indices/{ticker}/components', category: 'Indices', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'AXJO' }] },
  'indices/exposure': { responseKey: '01-market-data/indices/exposure', method: 'GET', label: 'Index Exposure', path: '/indices/{ticker}/exposure', category: 'Indices', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'AAPL' }] },
  'futures/gainers': { responseKey: '01-market-data/futures/gainers', method: 'GET', label: 'Top Gainers', path: '/futures/gainers', category: 'Futures', params: [{ name: 'days', required: false, type: 'number', location: 'query', defaultValue: '5' }, { name: 'limit', required: false, type: 'number', location: 'query', defaultValue: '10' }] },
  'futures/losers': { responseKey: '01-market-data/futures/losers', method: 'GET', label: 'Top Losers', path: '/futures/losers', category: 'Futures', params: [{ name: 'days', required: false, type: 'number', location: 'query', defaultValue: '5' }, { name: 'limit', required: false, type: 'number', location: 'query', defaultValue: '10' }] },
  'esg/ticker': { responseKey: '04-macros-and-signals/esg/score', method: 'GET', label: 'ESG Score', path: '/esg/{ticker}', category: 'ESG', description: 'Returns ESG scores for a ticker.', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'AAPL' }] },
  'sentiment/social': { responseKey: '04-macros-and-signals/sentiment/social', method: 'GET', label: 'Social Sentiment', path: '/sentiment/{ticker}/social', category: 'Sentiment', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'AAPL' }] },
  'sentiment/news': { responseKey: '04-macros-and-signals/sentiment/news', method: 'GET', label: 'News Sentiment', path: '/sentiment/{ticker}/news', category: 'Sentiment', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'AAPL' }] },
  'sentiment/analyst': { responseKey: '04-macros-and-signals/sentiment/analyst', method: 'GET', label: 'Analyst Sentiment', path: '/sentiment/{ticker}/analyst', category: 'Sentiment', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'AAPL' }] },
  'supplychain/customers': { responseKey: '04-macros-and-signals/supply-chain/customers', method: 'GET', label: 'Customers', path: '/supply-chain/{ticker}/customers', category: 'Supply Chain', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'AAPL' }] },
  'supplychain/peers': { responseKey: '04-macros-and-signals/supply-chain/peers', method: 'GET', label: 'Peers', path: '/supply-chain/{ticker}/peers', category: 'Supply Chain', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'AAPL' }] },
  'supplychain/suppliers': { responseKey: '04-macros-and-signals/supply-chain/suppliers', method: 'GET', label: 'Suppliers', path: '/supply-chain/{ticker}/suppliers', category: 'Supply Chain', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'AAPL' }] },
  'stocks/traffic': { responseKey: '04-macros-and-signals/web-traffic/traffic', method: 'GET', label: 'Web Traffic', path: '/web-traffic/{ticker}/traffic', category: 'Web Traffic', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'AAPL' }] },
  'profile/ticker': { responseKey: '03-fundamentals/profiles/asset-profile', method: 'GET', label: 'Asset Profile', path: '/profiles/{ticker}', category: 'Profiles', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'AAPL' }] },
  'profile/ticker/info': { responseKey: '03-fundamentals/profiles/summary-profile', method: 'GET', label: 'Company Info', path: '/profiles/{ticker}/info', category: 'Profiles', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'AAPL' }] },
  'profile/ticker/summary': { responseKey: '03-fundamentals/profiles/summary-detail', method: 'GET', label: 'Summary Detail', path: '/profiles/{ticker}/summary', category: 'Profiles', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'AAPL' }] },
  'profile/ticker/statistics': { responseKey: '03-fundamentals/profiles/key-statistics', method: 'GET', label: 'Key Statistics', path: '/profiles/{ticker}/statistics', category: 'Profiles', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'AAPL' }] },
  'profile/ticker/recommendation': { responseKey: '03-fundamentals/profiles/recommendation', method: 'GET', label: 'Recommendation Trend', path: '/profiles/{ticker}/recommendation', category: 'Profiles', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'AAPL' }] },
  'profile/ticker/calendar': { responseKey: '03-fundamentals/profiles/calendar-events', method: 'GET', label: 'Calendar Events', path: '/profiles/{ticker}/calendar', category: 'Profiles', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'AAPL' }] },
  'filings/ticker/forms': { responseKey: '03-fundamentals/filings/by-form', method: 'GET', label: 'Filings by Form', path: '/filings/{ticker}/{formType}', category: 'Filings', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'AAPL' }, { name: 'formType', required: true, type: 'string', location: 'path', defaultValue: '4' }, { name: 'startDate', required: true, type: 'string', location: 'query', defaultValue: '2024-01-01', description: 'Start date (YYYY-MM-DD)' }, { name: 'endDate', required: false, type: 'string', location: 'query', defaultValue: '2024-12-31', description: 'End date (YYYY-MM-DD), defaults to today' }] },
  'filings/list/forms': { responseKey: '03-fundamentals/filings/describe-forms', method: 'GET', label: 'List Forms', path: '/filings/list/forms', category: 'Filings', params: [] },
  'filings/search': { responseKey: '03-fundamentals/filings/search', method: 'GET', label: 'Search Filings', path: '/filings/search', category: 'Filings', params: [{ name: 'year', required: true, type: 'string', location: 'query', defaultValue: '2024' }, { name: 'quarter', required: true, type: 'string', location: 'query', defaultValue: '1' }, { name: 'form', required: false, type: 'string', location: 'query', defaultValue: '4' }, { name: 'ticker', required: false, type: 'string', location: 'query', defaultValue: 'AAPL' }] },
  'filings/ticker': { responseKey: '03-fundamentals/filings/recent', method: 'GET', label: 'Recent Filings', path: '/filings/{ticker}', category: 'Filings', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'AAPL' }, { name: 'form', required: false, type: 'string', location: 'query', description: 'Filter by form type (e.g. 10-K, 4)', defaultValue: '' }, { name: 'limit', required: false, type: 'number', location: 'query', defaultValue: '10' }] },
  'filings/document/sentiment': { responseKey: '03-fundamentals/filings/document-sentiment', method: 'GET', label: 'Document Sentiment', path: '/filings/document/sentiment', category: 'Filings', params: [{ name: 'documentId', required: true, type: 'string', location: 'query', defaultValue: 'aHR0cHM6Ly93d3cuc2VjLmdvdi9BcmNoaXZlcy9lZGdhci9kYXRhLzMyMDE5My8wMDAwMzIwMTkzMjYwMDAwMTMvYWFwbC0yMDI2MDMyOC5odG0=' }] },
  'filings/document/text': { responseKey: '03-fundamentals/filings/document-text', method: 'GET', label: 'Document Text', path: '/filings/document/text', category: 'Filings', description: 'Returns a JSON object with the source URL and plain text content of a filing document.', params: [{ name: 'documentId', required: true, type: 'string', location: 'query', defaultValue: 'aHR0cHM6Ly93d3cuc2VjLmdvdi9BcmNoaXZlcy9lZGdhci9kYXRhLzMyMDE5My8wMDAwMzIwMTkzMjYwMDAwMTMvYWFwbC0yMDI2MDMyOC5odG0=' }] },
  'financials/ticker/revenue': { responseKey: '03-fundamentals/financials/revenue', method: 'GET', label: 'Revenue', path: '/financials/{ticker}/revenue', category: 'Financials', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'AAPL' }, { name: 'periods', required: false, type: 'number', location: 'query', defaultValue: '4' }] },
  'financials/ticker/netincome': { responseKey: '03-fundamentals/financials/net-income', method: 'GET', label: 'Net Income', path: '/financials/{ticker}/netincome', category: 'Financials', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'AAPL' }, { name: 'periods', required: false, type: 'number', location: 'query', defaultValue: '4' }] },
  'financials/ticker/total/assets': { responseKey: '03-fundamentals/financials/total-assets', method: 'GET', label: 'Total Assets', path: '/financials/{ticker}/total/assets', category: 'Financials', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'AAPL' }, { name: 'periods', required: false, type: 'number', location: 'query', defaultValue: '4' }] },
  'financials/ticker/total/liabilities': { responseKey: '03-fundamentals/financials/total-liabilities', method: 'GET', label: 'Total Liabilities', path: '/financials/{ticker}/total/liabilities', category: 'Financials', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'AAPL' }, { name: 'periods', required: false, type: 'number', location: 'query', defaultValue: '4' }] },
  'financials/ticker/stockholdersequity': { responseKey: '03-fundamentals/financials/stockholders-equity', method: 'GET', label: "Stockholders' Equity", path: '/financials/{ticker}/stockholdersequity', category: 'Financials', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'AAPL' }, { name: 'periods', required: false, type: 'number', location: 'query', defaultValue: '4' }] },
  'financials/ticker/current/assets': { responseKey: '03-fundamentals/financials/current-assets', method: 'GET', label: 'Current Assets', path: '/financials/{ticker}/current/assets', category: 'Financials', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'AAPL' }, { name: 'periods', required: false, type: 'number', location: 'query', defaultValue: '4' }] },
  'financials/ticker/current/liabilities': { responseKey: '03-fundamentals/financials/current-liabilities', method: 'GET', label: 'Current Liabilities', path: '/financials/{ticker}/current/liabilities', category: 'Financials', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'AAPL' }, { name: 'periods', required: false, type: 'number', location: 'query', defaultValue: '4' }] },
  'financials/ticker/cashflow/operating': { responseKey: '03-fundamentals/financials/operating-cash-flow', method: 'GET', label: 'Operating Cash Flow', path: '/financials/{ticker}/cashflow/operating', category: 'Financials', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'AAPL' }, { name: 'periods', required: false, type: 'number', location: 'query', defaultValue: '4' }] },
  'financials/ticker/capitalexpenditures': { responseKey: '03-fundamentals/financials/capital-expenditures', method: 'GET', label: 'Capital Expenditures', path: '/financials/{ticker}/capitalexpenditures', category: 'Financials', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'AAPL' }, { name: 'periods', required: false, type: 'number', location: 'query', defaultValue: '4' }] },
  'financials/ticker/cashflow/free': { responseKey: '03-fundamentals/financials/free-cash-flow', method: 'GET', label: 'Free Cash Flow', path: '/financials/{ticker}/cashflow/free', category: 'Financials', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'AAPL' }, { name: 'periods', required: false, type: 'number', location: 'query', defaultValue: '4' }] },
  'financials/ticker/sharesoutstanding/basic': { responseKey: '03-fundamentals/financials/shares-outstanding-basic', method: 'GET', label: 'Shares Outstanding (Basic)', path: '/financials/{ticker}/sharesoutstanding/basic', category: 'Financials', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'AAPL' }, { name: 'periods', required: false, type: 'number', location: 'query', defaultValue: '4' }] },
  'financials/ticker/sharesoutstanding/diluted': { responseKey: '03-fundamentals/financials/shares-outstanding-diluted', method: 'GET', label: 'Shares Outstanding (Diluted)', path: '/financials/{ticker}/sharesoutstanding/diluted', category: 'Financials', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'AAPL' }, { name: 'periods', required: false, type: 'number', location: 'query', defaultValue: '4' }] },
  'financials/ticker/metrics': { responseKey: '03-fundamentals/financials/metrics', method: 'GET', label: 'Financial Metrics', path: '/financials/{ticker}/metrics', category: 'Financials', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'AAPL' }] },
  'financials/ticker/snapshot': { responseKey: '03-fundamentals/financials/snapshot', method: 'GET', label: 'Financial Snapshot', path: '/financials/{ticker}/snapshot', category: 'Financials', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'AAPL' }] },
  'financials/statements/balance': { responseKey: '03-fundamentals/financials/statement-balance', method: 'GET', label: 'Balance Sheet', path: '/financials/statements/{ticker}/balance', category: 'Financials', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'AAPL' }, { name: 'year', required: false, type: 'string', location: 'query', defaultValue: '' }, { name: 'quarter', required: false, type: 'string', location: 'query', defaultValue: '' }] },
  'financials/statements/income': { responseKey: '03-fundamentals/financials/statement-income', method: 'GET', label: 'Income Statement', path: '/financials/statements/{ticker}/income', category: 'Financials', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'AAPL' }, { name: 'year', required: false, type: 'string', location: 'query', defaultValue: '' }, { name: 'quarter', required: false, type: 'string', location: 'query', defaultValue: '' }] },
  'financials/statements/cashflow': { responseKey: '03-fundamentals/financials/statement-cashflow', method: 'GET', label: 'Cash Flow Statement', path: '/financials/statements/{ticker}/cashflow', category: 'Financials', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'AAPL' }, { name: 'year', required: false, type: 'string', location: 'query', defaultValue: '' }, { name: 'quarter', required: false, type: 'string', location: 'query', defaultValue: '' }] },
  'financials/dcf/value': { responseKey: '03-fundamentals/financials/dcf-value', method: 'GET', label: 'DCF Valuation', path: '/financials/dcf/{ticker}/value', category: 'Financials', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'AAPL' }] },
  'financials/dcf/rate': { responseKey: '03-fundamentals/financials/dcf-rate', method: 'GET', label: 'Discount Rate', path: '/financials/dcf/{ticker}/rate', category: 'Financials', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'AAPL' }] },
  'financials/ticker/eps': { responseKey: '03-fundamentals/financials/eps-historical', method: 'GET', label: 'EPS (Historical)', path: '/financials/{ticker}/eps', category: 'Financials', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'AAPL' }, { name: 'from', required: false, type: 'string', location: 'query', defaultValue: '' }, { name: 'to', required: false, type: 'string', location: 'query', defaultValue: '' }] },
  'financials/ticker/pe': { responseKey: '03-fundamentals/financials/pe-historical', method: 'GET', label: 'P/E Ratio (Historical)', path: '/financials/{ticker}/pe', category: 'Financials', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'AAPL' }, { name: 'from', required: false, type: 'string', location: 'query', defaultValue: '' }, { name: 'to', required: false, type: 'string', location: 'query', defaultValue: '' }, { name: 'frame', required: false, type: 'string', location: 'query', defaultValue: '' }] },
  'financials/ticker/marketcap': { responseKey: '03-fundamentals/financials/marketcap-historical', method: 'GET', label: 'Market Cap (Historical)', path: '/financials/{ticker}/marketcap', category: 'Financials', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'AAPL' }, { name: 'from', required: false, type: 'string', location: 'query', defaultValue: '' }, { name: 'to', required: false, type: 'string', location: 'query', defaultValue: '' }, { name: 'frame', required: false, type: 'string', location: 'query', defaultValue: '' }] },
  'financials/ticker/roe': { responseKey: '03-fundamentals/financials/roe-historical', method: 'GET', label: 'ROE (Historical)', path: '/financials/{ticker}/roe', category: 'Financials', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'AAPL' }, { name: 'from', required: false, type: 'string', location: 'query', defaultValue: '' }, { name: 'to', required: false, type: 'string', location: 'query', defaultValue: '' }] },
  'financials/ticker/ev': { responseKey: '03-fundamentals/financials/ev-historical', method: 'GET', label: 'Enterprise Value (Historical)', path: '/financials/{ticker}/ev', category: 'Financials', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'AAPL' }, { name: 'from', required: false, type: 'string', location: 'query', defaultValue: '' }, { name: 'to', required: false, type: 'string', location: 'query', defaultValue: '' }, { name: 'frame', required: false, type: 'string', location: 'query', defaultValue: '' }] },
  'financials/ticker/ebitda': { responseKey: '03-fundamentals/financials/ebitda-historical', method: 'GET', label: 'EBITDA (Historical)', path: '/financials/{ticker}/ebitda', category: 'Financials', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'AAPL' }, { name: 'from', required: false, type: 'string', location: 'query', defaultValue: '' }, { name: 'to', required: false, type: 'string', location: 'query', defaultValue: '' }] },
  'financials/ticker/de': { responseKey: '03-fundamentals/financials/de-historical', method: 'GET', label: 'Debt-to-Equity (Historical)', path: '/financials/{ticker}/de', category: 'Financials', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'AAPL' }, { name: 'from', required: false, type: 'string', location: 'query', defaultValue: '' }, { name: 'to', required: false, type: 'string', location: 'query', defaultValue: '' }] },
  'insiders/ticker/funds': { responseKey: '03-fundamentals/insiders/fund-ownership', method: 'GET', label: 'Fund Ownership', path: '/insiders/{ticker}/funds', category: 'Insiders', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'AAPL' }] },
  'insiders/ticker/individuals': { responseKey: '03-fundamentals/insiders/individual-holders', method: 'GET', label: 'Individual Insiders', path: '/insiders/{ticker}/individuals', category: 'Insiders', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'AAPL' }] },
  'insiders/ticker/institutions': { responseKey: '03-fundamentals/insiders/institution-ownership', method: 'GET', label: 'Institution Ownership', path: '/insiders/{ticker}/institutions', category: 'Insiders', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'AAPL' }] },
  'insiders/ticker/ownership': { responseKey: '03-fundamentals/insiders/ownership-breakdown', method: 'GET', label: 'Ownership Breakdown', path: '/insiders/{ticker}/ownership', category: 'Insiders', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'AAPL' }] },
  'insiders/ticker/activity': { responseKey: '03-fundamentals/insiders/net-share-activity', method: 'GET', label: 'Net Share Activity', path: '/insiders/{ticker}/activity', category: 'Insiders', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'AAPL' }] },
  'insiders/ticker/transactions': { responseKey: '03-fundamentals/insiders/transactions', method: 'GET', label: 'Insider Transactions', path: '/insiders/{ticker}/transactions', category: 'Insiders', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'AAPL' }] },
  'earnings/ticker/history': { responseKey: '03-fundamentals/earnings/history', method: 'GET', label: 'Earnings History', path: '/earnings/{ticker}/history', category: 'Earnings', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'AAPL' }] },
  'earnings/ticker/trend': { responseKey: '03-fundamentals/earnings/trend', method: 'GET', label: 'Earnings Trend', path: '/earnings/{ticker}/trend', category: 'Earnings', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'AAPL' }] },
  'earnings/ticker/index': { responseKey: '03-fundamentals/earnings/index-trend', method: 'GET', label: 'Index Trend', path: '/earnings/{ticker}/index', category: 'Earnings', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'AAPL' }] },
  'earnings/ticker/report': { responseKey: '03-fundamentals/earnings/report', method: 'GET', label: 'Earnings Report', path: '/earnings/{ticker}/report', category: 'Earnings', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'AAPL' }, { name: 'year', required: true, type: 'string', location: 'query', defaultValue: '2023' }, { name: 'quarter', required: true, type: 'string', location: 'query', defaultValue: '4' }] },
  'earnings/ticker/transcript': { responseKey: '03-fundamentals/earnings/transcript', method: 'GET', label: 'Earnings Transcript', path: '/earnings/{ticker}/transcript', category: 'Earnings', params: [{ name: 'ticker', required: true, type: 'string', location: 'path', defaultValue: 'AAPL' }, { name: 'year', required: true, type: 'string', location: 'query', defaultValue: '2024' }, { name: 'quarter', required: true, type: 'string', location: 'query', defaultValue: '2' }] },
  'earnings/transcript/sentiment': { responseKey: '03-fundamentals/earnings/transcript-sentiment', method: 'GET', label: 'Transcript Sentiment', path: '/earnings/transcript/sentiment', category: 'Earnings', params: [{ name: 'id', required: true, type: 'string', location: 'query', defaultValue: 'eyJ0aWNrZXIiOiJBQVBMIiwieWVhciI6IjIwMjQiLCJxdWFydGVyIjoiMiJ9' }] },

  'stocks.list(market)': { responseKey: '01-market-data/stocks/list-market', method: 'GET', label: 'List by Market', path: '/stocks/list/{column}', category: 'Stocks', params: [{ name: 'column', required: true, type: 'string', location: 'path', defaultValue: 'market', description: 'market | country | currency | exchange | sector | industry | type' }] },
  'stocks.list(country)': { responseKey: '01-market-data/stocks/list-country', method: 'GET', label: 'List by Country', path: '/stocks/list/{column}', category: 'Stocks', params: [{ name: 'column', required: true, type: 'string', location: 'path', defaultValue: 'country', description: 'market | country | currency | exchange | sector | industry | type' }] },
  'stocks.list(currency)': { responseKey: '01-market-data/stocks/list-currency', method: 'GET', label: 'List by Currency', path: '/stocks/list/{column}', category: 'Stocks', params: [{ name: 'column', required: true, type: 'string', location: 'path', defaultValue: 'currency', description: 'market | country | currency | exchange | sector | industry | type' }] },
  'stocks.list(sector)': { responseKey: '01-market-data/stocks/list-sector', method: 'GET', label: 'List by Sector', path: '/stocks/list/{column}', category: 'Stocks', params: [{ name: 'column', required: true, type: 'string', location: 'path', defaultValue: 'sector', description: 'market | country | currency | exchange | sector | industry | type' }] },
  'stocks.list(industry)': { responseKey: '01-market-data/stocks/list-industry', method: 'GET', label: 'List by Industry', path: '/stocks/list/{column}', category: 'Stocks', params: [{ name: 'column', required: true, type: 'string', location: 'path', defaultValue: 'industry', description: 'market | country | currency | exchange | sector | industry | type' }] },
  'stocks.list(type)': { responseKey: '01-market-data/stocks/list-type', method: 'GET', label: 'List by Type', path: '/stocks/list/{column}', category: 'Stocks', params: [{ name: 'column', required: true, type: 'string', location: 'path', defaultValue: 'type', description: 'market | country | currency | exchange | sector | industry | type' }] },
  'stocks.list(exchange)': { responseKey: '01-market-data/stocks/list-exchange', method: 'GET', label: 'List by Exchange', path: '/stocks/list/{column}', category: 'Stocks', params: [{ name: 'column', required: true, type: 'string', location: 'path', defaultValue: 'exchange', description: 'market | country | currency | exchange | sector | industry | type' }] },

  'etfs.list(market)': { responseKey: '01-market-data/ETFs/list-market', method: 'GET', label: 'List by Market', path: '/etfs/list/{column}', category: 'ETFs', params: [{ name: 'column', required: true, type: 'string', location: 'path', defaultValue: 'market', description: 'market | country | currency | exchange | sector | industry | type' }] },
  'etfs.list(country)': { responseKey: '01-market-data/ETFs/list-country', method: 'GET', label: 'List by Country', path: '/etfs/list/{column}', category: 'ETFs', params: [{ name: 'column', required: true, type: 'string', location: 'path', defaultValue: 'country', description: 'market | country | currency | exchange | sector | industry | type' }] },
  'etfs.list(currency)': { responseKey: '01-market-data/ETFs/list-currency', method: 'GET', label: 'List by Currency', path: '/etfs/list/{column}', category: 'ETFs', params: [{ name: 'column', required: true, type: 'string', location: 'path', defaultValue: 'currency', description: 'market | country | currency | exchange | sector | industry | type' }] },
  'etfs.list(sector)': { responseKey: '01-market-data/ETFs/list-sector', method: 'GET', label: 'List by Sector', path: '/etfs/list/{column}', category: 'ETFs', params: [{ name: 'column', required: true, type: 'string', location: 'path', defaultValue: 'sector', description: 'market | country | currency | exchange | sector | industry | type' }] },
  'etfs.list(industry)': { responseKey: '01-market-data/ETFs/list-industry', method: 'GET', label: 'List by Industry', path: '/etfs/list/{column}', category: 'ETFs', params: [{ name: 'column', required: true, type: 'string', location: 'path', defaultValue: 'industry', description: 'market | country | currency | exchange | sector | industry | type' }] },
  'etfs.list(type)': { responseKey: '01-market-data/ETFs/list-type', method: 'GET', label: 'List by Type', path: '/etfs/list/{column}', category: 'ETFs', params: [{ name: 'column', required: true, type: 'string', location: 'path', defaultValue: 'type', description: 'market | country | currency | exchange | sector | industry | type' }] },
  'etfs.list(exchange)': { responseKey: '01-market-data/ETFs/list-exchange', method: 'GET', label: 'List by Exchange', path: '/etfs/list/{column}', category: 'ETFs', params: [{ name: 'column', required: true, type: 'string', location: 'path', defaultValue: 'exchange', description: 'market | country | currency | exchange | sector | industry | type' }] },

  'indices.list(exchange)': { responseKey: '01-market-data/indices/list-exchange', method: 'GET', label: 'List by Exchange', path: '/indices/list/{column}', category: 'Indices', params: [{ name: 'column', required: true, type: 'string', location: 'path', defaultValue: 'exchange', description: 'exchange | timezone | country' }] },
  'indices.list(timezone)': { responseKey: '01-market-data/indices/list-timezone', method: 'GET', label: 'List by Timezone', path: '/indices/list/{column}', category: 'Indices', params: [{ name: 'column', required: true, type: 'string', location: 'path', defaultValue: 'timezone', description: 'exchange | timezone | country' }] },
  'indices.list(country)': { responseKey: '01-market-data/indices/list-country', method: 'GET', label: 'List by Country', path: '/indices/list/{column}', category: 'Indices', params: [{ name: 'column', required: true, type: 'string', location: 'path', defaultValue: 'country', description: 'exchange | timezone | country' }] },

  'futures.list(exchange)': { responseKey: '01-market-data/futures/list-exchange', method: 'GET', label: 'List by Exchange', path: '/futures/list/{column}', category: 'Futures', params: [{ name: 'column', required: true, type: 'string', location: 'path', defaultValue: 'exchange', description: 'exchange | currency | timezone | country' }] },
  'futures.list(currency)': { responseKey: '01-market-data/futures/list-currency', method: 'GET', label: 'List by Currency', path: '/futures/list/{column}', category: 'Futures', params: [{ name: 'column', required: true, type: 'string', location: 'path', defaultValue: 'currency', description: 'exchange | currency | timezone | country' }] },
  'futures.list(timezone)': { responseKey: '01-market-data/futures/list-timezone', method: 'GET', label: 'List by Timezone', path: '/futures/list/{column}', category: 'Futures', params: [{ name: 'column', required: true, type: 'string', location: 'path', defaultValue: 'timezone', description: 'exchange | currency | timezone | country' }] },
  'futures.list(country)': { responseKey: '01-market-data/futures/list-country', method: 'GET', label: 'List by Country', path: '/futures/list/{column}', category: 'Futures', params: [{ name: 'column', required: true, type: 'string', location: 'path', defaultValue: 'country', description: 'exchange | currency | timezone | country' }] },

  'forex.list(exchange)': { responseKey: '01-market-data/forex/list-exchange', method: 'GET', label: 'List by Exchange', path: '/forex/list/{column}', category: 'Forex', params: [{ name: 'column', required: true, type: 'string', location: 'path', defaultValue: 'exchange', description: 'exchange | rating | country' }] },
  'forex.list(rating)': { responseKey: '01-market-data/forex/list-rating', method: 'GET', label: 'List by Rating', path: '/forex/list/{column}', category: 'Forex', params: [{ name: 'column', required: true, type: 'string', location: 'path', defaultValue: 'rating', description: 'exchange | rating | country' }] },
  'forex.list(country)': { responseKey: '01-market-data/forex/list-country', method: 'GET', label: 'List by Country', path: '/forex/list/{column}', category: 'Forex', params: [{ name: 'column', required: true, type: 'string', location: 'path', defaultValue: 'country', description: 'exchange | rating | country' }] },

  'crypto.list(category)': { responseKey: '01-market-data/crypto/list-category', method: 'GET', label: 'List by Category', path: '/crypto/list/{column}', category: 'Crypto', params: [{ name: 'column', required: true, type: 'string', location: 'path', defaultValue: 'category', description: 'category | rating | type' }] },
  'crypto.list(rating)': { responseKey: '01-market-data/crypto/list-rating', method: 'GET', label: 'List by Rating', path: '/crypto/list/{column}', category: 'Crypto', params: [{ name: 'column', required: true, type: 'string', location: 'path', defaultValue: 'rating', description: 'category | rating | type' }] },
  'crypto.list(type)': { responseKey: '01-market-data/crypto/list-type', method: 'GET', label: 'List by Type', path: '/crypto/list/{column}', category: 'Crypto', params: [{ name: 'column', required: true, type: 'string', location: 'path', defaultValue: 'type', description: 'category | rating | type' }] },
};
